import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Gem, LayoutDashboard, Radar, FolderOpen, Settings, CalendarClock, Menu, LogOut, Users, MessageSquareText, ScanSearch, Newspaper } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/", label: "Übersicht", icon: LayoutDashboard, end: true, testId: "nav-overview" },
  { to: "/scanner", label: "Scanner", icon: Radar, testId: "nav-scanner" },
  { to: "/pruefen", label: "URL prüfen", icon: ScanSearch, testId: "nav-checkurl" },
  { to: "/zeitplan", label: "Zeitplan", icon: CalendarClock, testId: "nav-schedule" },
  { to: "/fallakte", label: "Fallakte", icon: FolderOpen, testId: "nav-cases" },
  { to: "/medien", label: "Medien-Monitoring", icon: Newspaper, testId: "nav-medien" },
  { to: "/erwaehnungen", label: "Erwähnungen", icon: MessageSquareText, testId: "nav-mentions" },
  { to: "/benutzer", label: "Benutzer", icon: Users, testId: "nav-users", adminOnly: true },
  { to: "/einstellungen", label: "Einstellungen", icon: Settings, testId: "nav-settings" },
];

export function BrandLogo({ light = false }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-10 w-10 items-center justify-center overflow-hidden rounded-md border ${light ? "border-white/20 bg-[#0A0A0A]" : "border-[#E8E4DF] bg-[#0A0A0A]"}`}>
        <img src="/stig-rock-gem.png" alt="STIG ROCK" className="h-7 w-auto object-contain" />
      </div>
      <div className="leading-tight">
        <p className={`font-heading text-base font-bold tracking-[0.18em] ${light ? "text-white" : "text-[#0A0A0A]"}`}>
          STIG <span className="font-medium text-[#A6A4A9]">ROCK</span>
        </p>
        <p className="text-[9px] uppercase tracking-[0.28em] text-[#A6A4A9]">Brand Protection</p>
      </div>
    </div>
  );
}

function NavList({ onNavigate }) {
  const { user } = useAuth();
  const items = NAV.filter((item) => !item.adminOnly || user?.role === "admin");
  return (
    <nav className="flex-1 px-4 py-5 space-y-1">
      {items.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          data-testid={item.testId}
          onClick={onNavigate}
          className={({ isActive }) =>
            `group flex items-center gap-3 rounded-md px-3.5 py-3 text-sm font-medium tracking-wide transition-all duration-300 ${
              isActive
                ? "bg-white/[0.07] text-white ring-1 ring-white/10"
                : "text-[#A6A4A9] hover:bg-white/[0.04] hover:text-white"
            }`
          }
        >
          {({ isActive }) => (
            <>
              <item.icon
                className={`h-[18px] w-[18px] transition-colors ${isActive ? "text-white" : "text-[#A6A4A9] group-hover:text-white"}`}
                strokeWidth={1.5}
              />
              {item.label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarInner({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col bg-[#0A0A0A] text-white">
      <div className="border-b border-white/10 px-6 py-7">
        <BrandLogo light />
      </div>

      <NavList onNavigate={onNavigate} />

      <div className="mx-4 mb-3 rounded-md border border-white/10 bg-white/[0.04] p-3">
        <p className="truncate text-xs font-semibold text-white">{user?.name || "Angemeldet"}</p>
        <p className="truncate text-[11px] text-[#A6A4A9]">{user?.email}</p>
        <button
          data-testid="logout-button"
          onClick={() => { logout(); onNavigate?.(); }}
          className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-md bg-white/[0.06] px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/10"
        >
          <LogOut className="h-3.5 w-3.5" /> Abmelden
        </button>
      </div>

      <div className="mx-4 mb-5 rounded-md border border-white/10 bg-white/[0.04] p-4">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-emerald-400 animate-soft-pulse" />
          <p className="text-xs font-semibold tracking-wide text-white">Monitor aktiv</p>
        </div>
        <p className="mt-1.5 text-[11px] leading-relaxed text-[#A6A4A9]">
          Durchsucht öffentlich indexierte Inhalte. Bewertung via KI.
        </p>
      </div>
    </div>
  );
}

export function Sidebar() {
  return (
    <aside className="sticky top-0 hidden h-screen w-64 shrink-0 lg:block">
      <SidebarInner />
    </aside>
  );
}

export function MobileNav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 flex items-center justify-between bg-[#0A0A0A] px-4 py-3 text-white lg:hidden">
      <BrandLogo light />
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <button data-testid="mobile-menu-button" className="rounded-md p-2 transition-colors hover:bg-white/10" aria-label="Menü öffnen">
            <Menu className="h-6 w-6" />
          </button>
        </SheetTrigger>
        <SheetContent side="left" className="w-64 border-0 p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarInner onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
    </header>
  );
}
